import { useCallback } from 'react';
import { useAppStore } from '../store';
import type { ClassificationCorrection, Transaction } from '../types';
import { classify } from '../services/classificationService';

export function useCorrections() {
  const corrections = useAppStore((state) => state.corrections);
  return corrections;
}

function normalizeDescription(description: string): string {
  return description
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '');
}

export function useAddCorrection() {
  const addCorrection = useAppStore((state) => state.addCorrection);

  return useCallback(
    (transaction: Transaction, newCategoryId: string) => {
      const autoResult = classify(transaction.description);
      const correction: ClassificationCorrection = {
        id: crypto.randomUUID(),
        transactionId: transaction.id,
        originalDescription: transaction.description,
        normalizedDescription: normalizeDescription(transaction.description),
        autoCategory: autoResult.categoryId,
        userCategory: newCategoryId,
        createdAt: new Date().toISOString(),
      };
      addCorrection(correction);
      return correction;
    },
    [addCorrection]
  );
}

export function useFindSimilarTransactions() {
  const transactions = useAppStore((state) => state.transactions);

  return useCallback(
    (transaction: Transaction): Transaction[] => {
      const normalized = normalizeDescription(transaction.description);
      const words = normalized.split(' ').filter((w) => w.length > 2);

      if (words.length === 0) return [];

      const primaryWord = words[0];

      return transactions.filter((t) => {
        if (t.id === transaction.id) return false;
        const tNormalized = normalizeDescription(t.description);
        return tNormalized.startsWith(primaryWord);
      });
    },
    [transactions]
  );
}

export function useApplyCategoryToSimilar() {
  const updateTransaction = useAppStore((state) => state.updateTransaction);
  const addCorrection = useAppStore((state) => state.addCorrection);

  return useCallback(
    (similarTransactions: Transaction[], newCategoryId: string): number => {
      let count = 0;
      similarTransactions.forEach((t) => {
        const autoResult = classify(t.description);
        const correction: ClassificationCorrection = {
          id: crypto.randomUUID(),
          transactionId: t.id,
          originalDescription: t.description,
          normalizedDescription: normalizeDescription(t.description),
          autoCategory: autoResult.categoryId,
          userCategory: newCategoryId,
          createdAt: new Date().toISOString(),
        };
        addCorrection(correction);
        updateTransaction(t.id, { categoryId: newCategoryId });
        count++;
      });
      return count;
    },
    [updateTransaction, addCorrection]
  );
}
